import NaatiClusterLinks from "@/components/NaatiClusterLinks";
// r24 Wave 2 — NAATI FAQ hub index (TH)
import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const CAT_LABEL_TH: Record<string, string> = {
  general: "ข้อมูลทั่วไป", cost: "ราคาและค่าใช้จ่าย", process: "ขั้นตอน / เวลา",
  docs: "เอกสารรายชนิด", validity: "อายุการใช้งาน", quality: "คุณภาพ / ตรวจสอบ",
  delivery: "การจัดส่ง", authority: "หน่วยงาน / วีซ่า",
};
const ORDER = ["general", "authority", "docs", "cost", "process", "quality", "delivery", "validity"];

export const Route = createFileRoute("/translate/naati/faq/")({
    loader: async () => {
    const { NAATI_FAQS } = await import("@/data/naati-faq-bank");
    return { __d: { NAATI_FAQS } };
  },
head: ({ loaderData }) => {
    const { NAATI_FAQS } = loaderData!.__d as typeof import("@/data/naati-faq-bank");
    const url = `${SITE}/translate/naati/faq`;
    const n = NAATI_FAQS.length;
    const title = `คำถามพบบ่อยเรื่องแปล NAATI — ${n} คำตอบจากทีม NYC`;
    const desc = `รวม ${n} คำถามที่ลูกค้าถามบ่อยสุดเรื่องแปล NAATI ขั้นตอน อายุใช้งาน การตรวจสอบตราประทับ และการยื่นต่อ DHA/BDM/AHPRA — เริ่มต้นง่าย ๆ แค่ทักไลน์ เราดูแลจนจบทุกขั้นตอน`;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/naati/faq/index", "th") }, { name: "description", content: ctaDesc(desc, "/translate/naati/faq/index", "th")},
        { property: "og:title", content: ctaTitle(title, "/translate/naati/faq/index", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/naati/faq/index", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: canonicalAndHreflang("/translate/naati/faq", "/en/translate/naati/faq"),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "th",
          mainEntity: NAATI_FAQS.map((f) => ({
            "@type": "Question", name: f.questionTh,
            acceptedAnswer: { "@type": "Answer", text: f.shortAnswerTh },
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "แปล NAATI", item: `${SITE}/translate/naati` },
            { "@type": "ListItem", position: 3, name: "คำถามพบบ่อย", item: url },
          ],
        }) },
      ],
    };
  },
  component: FaqHub,
});

function FaqHub() {
  const { NAATI_FAQS } = (Route.useLoaderData() as { __d: typeof import("@/data/naati-faq-bank") }).__d;
  const grouped = ORDER.map((cat) => ({
    cat, label: CAT_LABEL_TH[cat],
    items: NAATI_FAQS.filter((f) => f.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">หน้าแรก</Link> ·{" "}
        <Link to="/translate/naati" className="hover:underline">แปล NAATI</Link> ·{" "}
        <span className="text-slate-700">คำถามพบบ่อย</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold text-navy-deep mb-3">คำถามพบบ่อยเรื่องแปล NAATI</h1>
      <p data-speakable="true" className="text-lg text-slate-700 mb-8 direct-answer">
        รวม {NAATI_FAQS.length} คำถามที่ลูกค้าออสเตรเลีย-ไทยถามทีมเราบ่อยสุด — ตั้งแต่ &ldquo;ตราประทับ NAATI สีอะไร&rdquo;
        ไปจนถึง &ldquo;วีซ่า 189 ต้องแปลอะไรบ้าง&rdquo; แต่ละคำตอบเขียนจากประสบการณ์เคสจริง ไม่ใช่ AI generic
      </p>
      {grouped.map((g) => (
        <section key={g.cat} className="mb-10">
          <h2 className="text-2xl font-semibold text-navy-deep border-b border-gold pb-2 mb-4">
            {g.label} <span className="text-sm font-normal text-slate-500">({g.items.length})</span>
          </h2>
          <ul className="grid md:grid-cols-2 gap-3">
            {g.items.map((f) => (
              <li key={f.slug}>
                <Link
                  to="/translate/naati/faq/$slug" params={{ slug: f.slug }}
                  className="block border rounded-lg p-4 hover:border-gold hover:bg-amber-50/50 transition"
                >
                  <div className="font-medium text-navy-deep">{f.questionTh}</div>
                  <div className="text-sm text-slate-600 mt-1 line-clamp-2">{f.shortAnswerTh}</div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <div className="mt-8 p-6 bg-navy-deep text-white rounded-lg">
        <h2 className="text-xl font-semibold mb-2">ไม่เจอคำถามที่ต้องการ?</h2>
        <p>แชท LINE @nycli หรือดู <Link to="/translate/naati" className="underline text-gold">หน้าบริการแปล NAATI</Link> — ทีมตอบภายใน 15 นาทีเวลาทำการ</p>
      </div>
      <NaatiClusterLinks />
    </main>
  );
}
